import { Component, OnInit, OnDestroy, AfterViewInit, ViewChild, ChangeDetectorRef, SimpleChanges, HostListener, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { FormsModule, FormControl, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { EtiquettePilotageModel } from 'src/models/pilotage/etiquettePilotageModel';
import { EtiquetteService } from 'src/services/etiquette.service';
import { CreerEtiquetteComponent } from './creer-etiquette-dialog/creer-etiquette-dialog.component';
import { UtilisateurService } from 'src/services/utilisateur.service';
import { ReplaySubject, Subject, takeUntil } from 'rxjs';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { SupprimerEtiquetteDialogComponent } from './supprimer-etiquette-dialog/supprimer-etiquette-dialog.component';
import { ModifierEtiquetteDialogComponent } from './modifier-etiquette-dialog/modifier-etiquette-dialog.component';
import { ShowUtilisateursEtiquetteDialogComponent } from './show-utilisateurs-etiquette-dialog/show-utilisateurs-etiquette-dialog.component';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatPaginator, MatPaginatorIntl, PageEvent } from '@angular/material/paginator';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { MatMenuModule } from '@angular/material/menu';
import { ConfirmationExportDialogComponent } from '../../../commun/dialog/confirmation-export-dialog/confirmation-export-dialog.component';
import { CustomPaginatorIntlService } from 'src/services/custom-paginator-intl.service';

@Component({
    selector: 'app-etiquettes-managment',
    templateUrl: './etiquettes-managment.component.html',
    styleUrls: ['./etiquettes-managment.component.scss'],
    imports: [
        CommonModule,
        MatDialogModule,
        MatIconModule,
        MatSnackBarModule,
        MatProgressSpinnerModule,
        FormsModule,
        TranslateModule,
        MatFormFieldModule,
        MatSelectModule,
        NgxMatSelectSearchModule,
        ReactiveFormsModule,
        MatTooltipModule,
        MatTableModule,
        MatPaginator,
        MatSortModule,
        MatMenuModule
    ],
    providers: [
        {
            provide: MatPaginatorIntl,
            useClass: CustomPaginatorIntlService
        }
    ]
})
export class EtiquettesManagmentComponent implements OnInit, OnDestroy, AfterViewInit {
  tags: EtiquettePilotageModel[] = [];
  isLoading: boolean = false;
  searchTerm: string = '';
  selectedEntities: string[] = [];
  entities: string[] = [];
  entityFilterCtrl: FormControl = new FormControl();
  filteredEntities: ReplaySubject<string[]> = new ReplaySubject<string[]>(1);
  _onDestroy = new Subject<void>();
  readonly LANGS = ['fr', 'en', 'es'];

  displayedColumns: string[] = [
    'nom',
    'entreprise',
    'visibleSurCertificats',
    'nombreUtilisateurs',
    'actions'
  ];

  pageSizeOptions: number[] = [5, 10, 25, 100];

  dataSource!: MatTableDataSource<EtiquettePilotageModel>;

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  constructor(
    private translate: TranslateService,
    private changeDetectorRef: ChangeDetectorRef,
    public etiquetteService: EtiquetteService,
    private dialog: MatDialog,
    private utilisateurService: UtilisateurService
  ) {
    translate.setDefaultLang('fr');
    this.dataSource = new MatTableDataSource<EtiquettePilotageModel>([]);

  }

  ngOnInit(): void {
    this.loadTags();
    this.filteredEntities.next(this.entities.slice());
    this.entityFilterCtrl.valueChanges
      .pipe(takeUntil(this._onDestroy))
      .subscribe(() => {
        this.filterEntities();
      });
  }

  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;

    this.dataSource.sortingDataAccessor = (data: EtiquettePilotageModel, sortHeaderId: string) => {
      switch (sortHeaderId) {
        case 'nom':
          return this.etiquetteService.getNomLocalise(data.locales)?.toLowerCase() || '';
        case 'entreprise':
          return this.obtenirNomEntites(data.entiteIds).join(', ').toLowerCase();
        case 'visibleSurCertificats':
          return data.visibleSurCertificats ? 1 : 0;
        case 'nombreUtilisateurs':
          return data.nombreUtilisateurs || 0;
        default:
          return (data as any)[sortHeaderId];
      }
    };

    this.dataSource.filterPredicate = (data: EtiquettePilotageModel, filter: string) => {
      try {
        const filterObject = JSON.parse(filter);
        const entityMatch = !filterObject.selectedEntities?.length ||
          this.obtenirNomEntites(data.entiteIds).some((nom: string) => filterObject.selectedEntities.includes(nom));
        const searchMatch = !filterObject.searchTerm ||
          this.LANGS.some(lang => data.locales && data.locales[lang] && data.locales[lang].toLowerCase().includes(filterObject.searchTerm.toLowerCase()));
        return entityMatch && searchMatch;
      } catch {
        return true;
      }
    };

    this.changeDetectorRef.detectChanges();
  }

  toggleMenu(element: EtiquettePilotageModel): void {
    element.isMenuOpen = !element.isMenuOpen;
    this.tags.forEach((tag) => {
      if (tag.etiquetteId !== element.etiquetteId) {
        tag.isMenuOpen = false;
      }
    });
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    const isInsideMenu = target.closest('.menu-container');
    if (!isInsideMenu) {
      this.tags.forEach(tag => tag.isMenuOpen = false);
    }
  }

  refreshDataSource(): void {
    this.dataSource.data = this.tags;
    this.applyFilters();
  }

  private setDefaultFilters(): void {

  }

  ngOnDestroy() {
    this._onDestroy.next();
    this._onDestroy.complete();
  }

  private filterEntities() {
    if (!this.entities) {
      return;
    }
    let search = this.entityFilterCtrl.value;
    if (!search) {
      this.filteredEntities.next(this.entities.slice());
      return;
    } else {
      search = search.toLowerCase();
    }
    this.filteredEntities.next(
      this.entities.filter(entity => entity.toLowerCase().indexOf(search) > -1));
  }

  loadTags(): void {
    this.isLoading = true;

    this.utilisateurService.obtenirContexte().subscribe(
      res => {
        this.utilisateurService.contexteUtilisateur = res;
        this.entities = this.utilisateurService.contexteUtilisateur.entitesAccessibles
          .map(e => this.formaterNomEntite(e.nom, e.estActif))
          .sort((a, b) => a.localeCompare(b, 'fr', { sensitivity: 'base' }));

        this.etiquetteService.obtenirEtiquettes().subscribe(
          (etiquettes) => {
            this.tags = etiquettes;
            this.dataSource.data = this.tags;
            this.setDefaultFilters();
            this.applyFilters();
            this.filteredEntities.next(this.entities.slice());
            this.isLoading = false;

            setTimeout(() => {
              if (this.sort && this.paginator) {
                this.dataSource.sort = this.sort;
                this.dataSource.paginator = this.paginator;
              }
            });
          },
          (error) => {
            console.error('Error loading tags:', error);
            this.isLoading = false;
          }
        );
      }
    );
  }

  applyFilters(): void {
    const filterValue = {
      searchTerm: this.searchTerm,
      selectedEntities: this.selectedEntities
    };
    this.dataSource.filter = JSON.stringify(filterValue);

    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  resetFilters(): void {
    this.searchTerm = '';
    this.selectedEntities = [];
    this.applyFilters();
  }

  openCreateTagDialog(): void {
    const dialogRef = this.dialog.open(CreerEtiquetteComponent, {
      width: '700px',
      disableClose: true
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.loadTags();
      }
    });
  }

  obtenirNomEntites(entiteIds: number[]): string[] {
    if (entiteIds && entiteIds.length > 0) {
      return entiteIds.map(id => {
        const entite = this.utilisateurService.contexteUtilisateur.toutesLesEntites.find(e => e.entiteId === id);
        return entite ? this.formaterNomEntite(entite.nom, entite.estActif) : '';
      });
    }
    return [''];
  }

  formaterNomEntite(nom: string, estActif: boolean): string {
    return estActif ? nom : `${nom} (${this.translate.instant('ENTITE_INACTIF')})`;
  }

  obtenirCouleur(hexColor: string): string {
    return this.etiquetteService.obtenirCouleur(hexColor);
  }

  editTag(tagId: number): void {
    const tagToEdit = this.tags.find(tag => tag.etiquetteId === tagId);
    if (tagToEdit) {
      const dialogRef = this.dialog.open(ModifierEtiquetteDialogComponent, {
        width: '700px',
        data: tagToEdit,
        disableClose: true
      });

      dialogRef.afterClosed().subscribe(result => {
        if (result) {
          this.loadTags();
        }
      });
    }
  }

  deleteTag(etiquetteId: number): void {
    const dialogRef = this.dialog.open(SupprimerEtiquetteDialogComponent, {
      data: { etiquetteId: etiquetteId }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === true) {
        this.loadTags();
      }
    });
  }


  exporterVersExcel(): void {
    const ids = this.dataSource.filteredData.map(e => e.etiquetteId);
    if (!ids.length) return;
    const dialogRef = this.dialog.open(ConfirmationExportDialogComponent, {
      width: '600px',
      data: { exportLabelKey: 'TAGS_MANAGEMENT.EXPORT_EXCEL' }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.isLoading = true;
        const codeLangue = this.translate.currentLang || 'fr';
        this.etiquetteService.exporterEtiquettes(ids, codeLangue).subscribe(blob => {
          const now = new Date();
          const pad = (n: number) => n.toString().padStart(2, '0');
          const datetime = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}_${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
          const filename = `etiquettes_${datetime}.xlsx`;
          const link = document.createElement('a');
          link.href = window.URL.createObjectURL(blob);
          link.download = filename;
          link.click();
          window.URL.revokeObjectURL(link.href);
          this.isLoading = false;
        }, () => {
          this.isLoading = false;
        });
      }
    });
  }

  exporterVersPdf(): void {
    const ids = this.dataSource.filteredData.map(e => e.etiquetteId);
    if (!ids.length) return;
    const dialogRef = this.dialog.open(ConfirmationExportDialogComponent, {
      width: '600px',
      data: { exportLabelKey: 'TAGS_MANAGEMENT.EXPORT_PDF' }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.isLoading = true;
        const codeLangue = this.translate.currentLang || 'fr';
        this.etiquetteService.exporterEtiquettesPdf(ids, codeLangue).subscribe(blob => {
          const now = new Date();
          const pad = (n: number) => n.toString().padStart(2, '0');
          const datetime = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}_${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
          const filename = `etiquettes_${datetime}.pdf`;
          const link = document.createElement('a');
          link.href = window.URL.createObjectURL(blob);
          link.download = filename;
          link.click();
          window.URL.revokeObjectURL(link.href);
          this.isLoading = false;
        }, () => {
          this.isLoading = false;
        });
      }
    });
  }

  get estRootLectureSeule(): boolean {
    return this.utilisateurService.selectionEntiteUtilisateur?.estRootLectureSeule || false;
  }

  showUtilisateursEtiquette(etiquette: EtiquettePilotageModel): void {
    const dialogRef = this.dialog.open(ShowUtilisateursEtiquetteDialogComponent, {
      width: '50vw',
      maxWidth: '800px',
      disableClose: true,
      data: { etiquette: etiquette }
    });
  }

  nombreUtilisateurs(etiquetteId: number): number {
    return this.tags.find(e => e.etiquetteId === etiquetteId)?.nombreUtilisateurs || 0;
  }
}
